import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatDistanceToNow } from 'date-fns';
import type { ChatMessage as ChatMessageType } from '@/hooks/useChat';

interface ChatMessageProps {
  message: ChatMessageType;
}

export function ChatMessage({ message }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={`flex flex-col gap-1 ${isUser ? 'items-end' : 'items-start'}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap break-words">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none break-words
            [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>table]:my-2
            [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm
            [&>table]:text-xs [&>table]:border-collapse
            [&_th]:border [&_th]:border-border [&_th]:px-2 [&_th]:py-1 [&_th]:bg-muted/50
            [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1
            [&>pre]:bg-muted [&>pre]:rounded [&>pre]:p-2 [&>pre]:overflow-x-auto
            [&>code]:bg-muted [&>code]:px-1 [&>code]:rounded [&>code]:text-xs">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              skipHtml
              disallowedElements={['script', 'iframe', 'object', 'embed', 'style']}
            >
              {message.content}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Actions taken chips */}
      {message.actionsTaken && message.actionsTaken.length > 0 && (
        <div className="flex flex-wrap gap-1 max-w-[85%]">
          {message.actionsTaken.map((action, i) => (
            <span
              key={i}
              className="text-[10px] text-muted-foreground bg-muted/60 rounded px-1.5 py-0.5"
            >
              ✓ {action}
            </span>
          ))}
        </div>
      )}

      <span className="text-[10px] text-muted-foreground px-1">
        {formatDistanceToNow(message.timestamp, { addSuffix: true })}
      </span>
    </div>
  );
}
